import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AppConfig } from "../../../AppConfig";
import { PageRoutes } from "../../../constants/routes";
import { LocalStorageManager } from "../../../core/storage/LocalStorageManager";
import { appActions } from "../../../ducks/app";
import { authActions, authSelectors } from "../../../ducks/auth";
import { campaignSelectors } from "../../../ducks/campaign";
import { AppCoupon, couponActions } from "../../../ducks/coupon";
import { storeActions, StoreSearchType } from "../../../ducks/store";
import { useBanner } from "../../../hooks/useBanner";
import { useEarlyBird } from "../../../hooks/useEarlyBird";
import { useNavigation } from "../../../hooks/useNavigation";

export const useHome = () => {
  const dispatch = useDispatch();
  const { navigateToPage } = useNavigation();
  const location = useLocation();
  const { redirectPath } = location.state ?? {};
  const query = new URLSearchParams(location.search);
  const currentActiveCampaign = useSelector(campaignSelectors.activeCampaign);
  const isLoggedIn = useSelector(authSelectors.isLoggedIn);
  const {
    checkCampaignStatus,
    campaign,
    handleWhenHasEarlyBirdData,
    isInPageToShowModal,
    menusInCart,
  } = useEarlyBird();

  const { banners } = useBanner();

  useEffect(() => {
    if (location.state?.isAppLinkage) {
      navigateToPage(PageRoutes.HOME, true);
      const menusEarlyBirdInCart = menusInCart?.some(
        (menu) => menu.isEarlyBird,
      );
      const hasEarlyBirdData = !!campaign || !!menusEarlyBirdInCart;
      if (isInPageToShowModal && hasEarlyBirdData) handleWhenHasEarlyBirdData();
      return;
    }
  }, [location.state]);

  useEffect(() => {
    if (!currentActiveCampaign) return;

    const unselectedAppCoupons = LocalStorageManager.Instance.getObject<
      string[]
    >(AppConfig.Instance.LocalStorageKey["unselectedAppCoupons"]);
    const selectedAppCoupons = LocalStorageManager.Instance.getObject<
      AppCoupon[]
    >(AppConfig.Instance.LocalStorageKey["selectedAppCoupons"]);

    if (
      (!unselectedAppCoupons || unselectedAppCoupons.length === 0) &&
      (!selectedAppCoupons || selectedAppCoupons.length === 0) &&
      !location.state?.isRegular &&
      !campaign
    ) {
      navigateToPage(PageRoutes.EARLY_BIRD_CAMPAIGN);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!redirectPath) return;

    setTimeout(() => {
      dispatch(
        appActions.showModal.started({
          title: "ログインしてください",
          nextButtonLabel: "ログイン",
          handleNext: () => {
            navigateToPage(PageRoutes.LOGIN, false, { redirectPath });
            dispatch(appActions.closeModal());
          },
          closeButtonLabel: "閉じる",
          handleClose: () => {
            window.history.replaceState(null, "", location.hash);
            dispatch(appActions.closeModal());
          },
        }),
      );
    }, 100);
  }, [redirectPath]);

  useEffect(() => {
    const appToken = query.get("app_token");
    const encryptedCoupons = query.get("q");

    if (!encryptedCoupons) return;

    if (location.pathname === PageRoutes.HOME) {
      const token = appToken
        ? `&app_token=${encodeURIComponent(appToken)}`
        : "";
      navigateToPage(
        PageRoutes.APP_LINKAGE_COUPON +
          "?q=" +
          encodeURIComponent(encryptedCoupons) +
          token,
        true,
      );
    }

    if (location.pathname === PageRoutes.APP_LINKAGE_COUPON) {
      dispatch(appActions.showLoading());
      if (!!appToken) {
        LocalStorageManager.Instance.saveObject(
          AppConfig.Instance.LocalStorageKey["appToken"],
          appToken,
        );
        if (isLoggedIn) {
          dispatch(
            authActions.saveExternalId.started({
              app_token: appToken,
              onSuccess: () => {
                dispatch(
                  couponActions.redeemCoupon.started({
                    q: encodeURIComponent(encryptedCoupons),
                    onComplete: () => dispatch(appActions.hideLoading()),
                    onSuccess: () =>
                      navigateToPage(PageRoutes.HOME, true, {
                        isAppLinkage: true,
                      }),
                  }),
                );
              },
            }),
          );
          return;
        }
      }
      dispatch(
        couponActions.redeemCoupon.started({
          q: encodeURIComponent(encryptedCoupons),
          onComplete: () => dispatch(appActions.hideLoading()),
          onSuccess: () =>
            navigateToPage(PageRoutes.HOME, true, { isAppLinkage: true }),
        }),
      );
    }
  }, [location.pathname, location.search]);

  const handleSearchByAddress = useCallback(
    (prefecture: string, city: string) => {
      checkCampaignStatus({
        onOk: () => {
          const conditions = [
            {
              name: "都道府県",
              operator: "equal",
              value: prefecture,
            },
          ];

          if (city) {
            conditions.push({
              name: "市区町村",
              operator: "equal",
              value: city,
            });
          }

          dispatch(appActions.showLoading());
          dispatch(
            storeActions.getStores.started({
              conditions,
              early_bird: !!campaign ? 1 : undefined,
              onComplete: () => dispatch(appActions.hideLoading()),
              onSuccess: (stores) => {
                if (stores.length === 0) {
                  dispatch(
                    appActions.showModal.started({
                      title: "該当する店舗がありません",
                      subTitle: "検索内容を変更してもう一度お試しください。",
                      nextButtonLabel: "閉じる",
                      handleNext: () => dispatch(appActions.closeModal()),
                    }),
                  );
                } else {
                  if (city) {
                    navigateToPage(
                      PageRoutes.SHOP_SEARCH_2.replace(
                        ":type",
                        StoreSearchType.Address.valueOf(),
                      )
                        .replace(":query1", prefecture)
                        .replace(":query2", city),
                    );
                  } else {
                    navigateToPage(
                      PageRoutes.SHOP_SEARCH.replace(
                        ":type",
                        StoreSearchType.Address.valueOf(),
                      ).replace(":query", prefecture),
                    );
                  }
                }
              },
            }),
          );
        },
      });
    },
    [campaign, checkCampaignStatus],
  );

  const handleSearchByText = useCallback(
    (text: string) => {
      checkCampaignStatus({
        onOk: () => {
          dispatch(appActions.showLoading());
          dispatch(
            storeActions.getStores.started({
              conditions: [
                {
                  name: "店舗名・住所名",
                  operator: "like",
                  value: text,
                },
              ],
              early_bird: !!campaign ? 1 : undefined,
              onComplete: () => dispatch(appActions.hideLoading()),
              onSuccess: (stores) => {
                if (stores.length === 0) {
                  dispatch(
                    appActions.showModal.started({
                      title: "該当する店舗がありません",
                      subTitle: "検索内容を変更してもう一度お試しください。",
                      nextButtonLabel: "閉じる",
                      handleNext: () => dispatch(appActions.closeModal()),
                    }),
                  );
                } else {
                  navigateToPage(
                    PageRoutes.SHOP_SEARCH.replace(
                      ":type",
                      StoreSearchType.Text.valueOf(),
                    ).replace(":query", text),
                  );
                }
              },
            }),
          );
        },
      });
    },
    [campaign, checkCampaignStatus],
  );

  const handleSearchByMyStore = useCallback((address: string) => {
    navigateToPage(
      PageRoutes.SHOP_SEARCH.replace(
        ":type",
        StoreSearchType.MyStore.valueOf(),
      ).replace(":query", address),
    );
  }, []);

  const handleSearchByLocation = useCallback(() => {
    navigateToPage(PageRoutes.SHOP_LOCATION_SEARCH);
  }, []);

  return {
    campaign,
    banners,
    handleSearchByAddress,
    handleSearchByText,
    handleSearchByLocation,
    handleSearchByMyStore,
  };
};
